/**
 * Romanian formatting helpers — money in RON, weights in kg, RO dates.
 * All user-facing numbers use tabular figures (see `.nums` utility).
 */

import type { ComandaStatus } from '@/db/dexie'

export const RO_LUNI_SCURT = [
  'ian.',
  'feb.',
  'mar.',
  'apr.',
  'mai',
  'iun.',
  'iul.',
  'aug.',
  'sep.',
  'oct.',
  'nov.',
  'dec.',
] as const

export const RO_LUNI = [
  'ianuarie',
  'februarie',
  'martie',
  'aprilie',
  'mai',
  'iunie',
  'iulie',
  'august',
  'septembrie',
  'octombrie',
  'noiembrie',
  'decembrie',
] as const

export const RO_ZILE = [
  'duminică',
  'luni',
  'marți',
  'miercuri',
  'joi',
  'vineri',
  'sâmbătă',
] as const

export const RO_ZILE_SCURT = ['dum', 'lun', 'mar', 'mie', 'joi', 'vin', 'sâm'] as const

type DateLike = Date | string | number

export function toDate(d: DateLike): Date {
  return d instanceof Date ? d : new Date(d)
}

/** Today, as a live Date. Single source of "now" for the demo. */
export function azi(): Date {
  return new Date()
}

export function laMiezulNoptii(d: DateLike = azi()): Date {
  const x = toDate(d)
  x.setHours(0, 0, 0, 0)
  return x
}

export function plusZile(n: number, from: DateLike = azi()): Date {
  const x = toDate(from)
  x.setDate(x.getDate() + n)
  return x
}

export const ieri = () => plusZile(-1)
export const maine = () => plusZile(1)

/** ISO yyyy-mm-dd (stable for seeding relative dates). */
export function isoZi(d: DateLike = azi()): string {
  const x = toDate(d)
  const m = String(x.getMonth() + 1).padStart(2, '0')
  const day = String(x.getDate()).padStart(2, '0')
  return `${x.getFullYear()}-${m}-${day}`
}

export function esteAzi(d: DateLike): boolean {
  return isoZi(d) === isoZi(azi())
}

const ronFmt = new Intl.NumberFormat('ro-RO', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const kgFmt = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 1 })
const intFmt = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 })

/** `1.234,56 lei` */
export function fmtRON(n: number): string {
  return `${ronFmt.format(Number.isFinite(n) ? n : 0)} lei`
}

/** `1.234,56` without the currency suffix (for tables that label the column). */
export function fmtSuma(n: number): string {
  return ronFmt.format(Number.isFinite(n) ? n : 0)
}

/** `20 kg` / `12,5 kg` */
export function fmtKg(n: number): string {
  return `${kgFmt.format(Number.isFinite(n) ? n : 0)} kg`
}

export function fmtNumar(n: number): string {
  return intFmt.format(Number.isFinite(n) ? n : 0)
}

/** `24 iul. 2026` */
export function fmtData(d: DateLike): string {
  const x = toDate(d)
  return `${x.getDate()} ${RO_LUNI_SCURT[x.getMonth()]} ${x.getFullYear()}`
}

/** `24 iulie` — no year */
export function fmtDataLunga(d: DateLike): string {
  const x = toDate(d)
  return `${x.getDate()} ${RO_LUNI[x.getMonth()]}`
}

/** `mar., 24 iul.` */
export function fmtDataScurt(d: DateLike): string {
  const x = toDate(d)
  return `${RO_ZILE_SCURT[x.getDay()]}, ${x.getDate()} ${RO_LUNI_SCURT[x.getMonth()]}`
}

/** `14:30` */
export function fmtOra(d: DateLike): string {
  const x = toDate(d)
  return `${String(x.getHours()).padStart(2, '0')}:${String(x.getMinutes()).padStart(2, '0')}`
}

/** Friendly relative label: azi / mâine / ieri / else a short date. */
export function fmtDataRelativa(d: DateLike): string {
  const target = isoZi(d)
  if (target === isoZi(azi())) return 'azi'
  if (target === isoZi(maine())) return 'mâine'
  if (target === isoZi(ieri())) return 'ieri'
  return fmtData(d)
}

// ---------------------------------------------------------------------------
//  Order-status metadata — labels + warm-editorial colors for the funnel.
// ---------------------------------------------------------------------------

export type StatusTone = 'amber' | 'pine' | 'tomato' | 'taupe' | 'ink'

export interface StatusMeta {
  label: string
  tone: StatusTone
  /** soft chip background */
  bg: string
  /** chip text / icon */
  fg: string
  /** status dot */
  dot: string
}

/** Funnel order — ORDER MATTERS. */
export const STATUS_ORDER: readonly ComandaStatus[] = [
  'Nou',
  'Confirmat',
  'În pregătire',
  'Pe drum',
  'Livrat',
  'Facturat',
]

export const statusMeta: Record<ComandaStatus, StatusMeta> = {
  Nou: {
    label: 'Nou',
    tone: 'amber',
    bg: 'oklch(0.95 0.05 82)',
    fg: 'oklch(0.44 0.11 66)',
    dot: 'oklch(0.77 0.132 72)',
  },
  Confirmat: {
    label: 'Confirmat',
    tone: 'pine',
    bg: 'oklch(0.93 0.04 156)',
    fg: 'oklch(0.43 0.082 156)',
    dot: 'oklch(0.55 0.09 156)',
  },
  'În pregătire': {
    label: 'În pregătire',
    tone: 'amber',
    bg: 'oklch(0.945 0.055 96)',
    fg: 'oklch(0.46 0.09 92)',
    dot: 'oklch(0.72 0.12 96)',
  },
  'Pe drum': {
    label: 'Pe drum',
    tone: 'tomato',
    bg: 'oklch(0.935 0.045 46)',
    fg: 'oklch(0.5 0.13 42)',
    dot: 'oklch(0.64 0.16 42)',
  },
  Livrat: {
    label: 'Livrat',
    tone: 'pine',
    bg: 'oklch(0.915 0.052 150)',
    fg: 'oklch(0.4 0.09 150)',
    dot: 'oklch(0.52 0.1 150)',
  },
  Facturat: {
    label: 'Facturat',
    tone: 'ink',
    bg: 'oklch(0.94 0.012 80)',
    fg: 'oklch(0.4 0.02 70)',
    dot: 'oklch(0.55 0.02 72)',
  },
}

export function statusLabel(s: ComandaStatus): string {
  return statusMeta[s]?.label ?? s
}

/** Next status in the funnel, or null if terminal (Facturat). */
export function statusUrmator(s: ComandaStatus): ComandaStatus | null {
  const i = STATUS_ORDER.indexOf(s)
  if (i < 0 || i >= STATUS_ORDER.length - 1) return null
  return STATUS_ORDER[i + 1] ?? null
}

export function statusIndex(s: ComandaStatus): number {
  return STATUS_ORDER.indexOf(s)
}
